import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, Space, Tag, Modal, Form, message, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserRole } from '../utils/cognitoUtils';
import { useApp } from '../contexts/AppContext';
import apiService, { User } from '../services/api';
import { isAdmin, getRoleLabel, getRoleColor, getBusinessAreaLabel } from '../utils/roleUtils';
import './Users.css';

const { Option } = Select;
const { Title } = Typography;

const Users: React.FC = () => {
  const { user: currentUser } = useCognitoAuth();
  const { refreshCalendarData } = useApp();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '', email: '', role: '', business_area: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentUser && isAdmin(getUserRole(currentUser))) {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const data = await apiService.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error('Erro ao carregar utilizadores.');
      setUsers([]);
    }
    setLoading(false);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // Convert role to proper case for the form
    form.setFieldsValue({
      ...user,
      role: user.role // The role is already in proper case from backend
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: 'Confirmar eliminação de utilizador',
      content: 'Tem certeza que deseja eliminar este utilizador? O utilizador será removido de todas as listas mas todos os dados associados serão preservados.',
      onOk: async () => {
        try {
          const response = await apiService.deleteUser(id);
          if (response.success) {
            message.success(response.message || 'Utilizador eliminado com sucesso!');
            fetchUsers();
          } else {
            message.error(response.message || 'Erro ao eliminar utilizador.');
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao eliminar utilizador.';
          message.error(errorMessage);
        }
      },
    });
  };

  const handleResendInvitation = async (id: number, email: string) => {
    Modal.confirm({
      title: 'Reenviar Convite',
      content: `Tem certeza que deseja reenviar o email de convite para ${email}?`,
      onOk: async () => {
        try {
          const response = await apiService.resendUserInvitation(id);
          if (response.success) {
            message.success(response.message || 'Email de convite reenviado com sucesso!');
          } else {
            message.error(response.message || 'Erro ao reenviar convite.');
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao reenviar convite.';
          message.error(errorMessage);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        const response = await apiService.updateUser(editingUser.id, values);
        if (response.success) {
          message.success(response.message || 'Utilizador atualizado com sucesso!');
          setShowModal(false);
          setEditingUser(null);
          form.resetFields();
          fetchUsers();
          refreshCalendarData();
        } else {
          message.error(response.message || 'Erro ao atualizar utilizador.');
        }
      } else {
        // Remove password - Cognito will handle it
        const userData = {
          ...values
        };
        const response = await apiService.createUser(userData);
        if (response.success) {
          message.success(response.message || 'Utilizador criado com sucesso! Email de convite foi enviado para definir a palavra-passe.');
          setShowModal(false);
          setEditingUser(null);
          form.resetFields();
          fetchUsers();
          refreshCalendarData();
        } else {
          message.error(response.message || 'Erro ao criar utilizador.');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar utilizador.';
      message.error(errorMessage);
    }
  };



  const filteredUsers = Array.isArray(users) ? users.filter(u =>
    (!filters.name || u.username.toLowerCase().includes(filters.name.toLowerCase())) &&
    (!filters.email || u.email.toLowerCase().includes(filters.email.toLowerCase())) &&
    (!filters.role || u.role === filters.role) &&
    (!filters.business_area || u.business_area === filters.business_area)
  ) : [];

  const columns = [
    { 
      title: 'Nome', 
      dataIndex: 'username', 
      key: 'username',
      filterDropdown: () => (
        <Input
          placeholder="Filtrar nome"
          value={filters.name}
          onChange={e => setFilters({ ...filters, name: e.target.value })}
          style={{ width: 130, marginBottom: 8, display: 'block' }}
        />
      ),
      onFilter: (value: any, record: User) => record.username.toLowerCase().includes(value.toLowerCase()),
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email',
      filterDropdown: () => (
        <Input
          placeholder="Filtrar email"
          value={filters.email}
          onChange={e => setFilters({ ...filters, email: e.target.value })}
          style={{ width: 160, marginBottom: 8, display: 'block' }}
        />
      ),
      onFilter: (value: any, record: User) => record.email.toLowerCase().includes(value.toLowerCase()),
    },
    { 
      title: 'Função', 
      dataIndex: 'role', 
      key: 'role',
      filters: [
        { text: 'Admin', value: 'Admin' },
        { text: 'Diretor Comercial', value: 'Diretor Comercial' },
        { text: 'Team Leader', value: 'Team Leader' },
        { text: 'Gestor de Equipa', value: 'Gestor de Equipa' },
        { text: 'Gestor de Equipa Júnior', value: 'Gestor de Equipa Júnior' },
        { text: 'Gestor Comercial', value: 'Gestor Comercial' },
      ],
      onFilter: (value: any, record: User) => record.role === value,
      render: (role: string) => (
        <Tag 
          style={{
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
            fontWeight: 500,
            padding: '4px 12px',
            borderRadius: '6px'
          }}
        >
          {getRoleLabel(role)}
        </Tag>
      ),
    },
    { 
      title: 'Área de Negócio', 
      dataIndex: 'business_area', 
      key: 'business_area',
      filters: [
        { text: 'Admin', value: 'Admin' },
        { text: 'Telecomunicações', value: 'telecommunications' },
        { text: 'Energia', value: 'energy_solutions' },
      ],
      onFilter: (value: any, record: User) => record.business_area === value,
      render: (area: string) => area ? (
        <Tag 
          style={{
            background: area === 'telecommunications' 
              ? 'rgba(59, 130, 246, 0.4)' 
              : 'rgba(34, 197, 94, 0.4)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
            fontWeight: 500,
            padding: '4px 12px',
            borderRadius: '6px'
          }}
        >
          {getBusinessAreaLabel(area)}
        </Tag>
      ) : (
        <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>-</span>
      ),
    },
    { 
      title: 'Ações', 
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: User) => (
        <Space size="small">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            size="small"
            style={{ 
              background: 'rgba(255, 255, 255, 0.2)', 
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff'
            }}
          />
          {record.cognito_enabled && (
            <Button 
              icon={<MailOutlined />} 
              onClick={() => handleResendInvitation(record.id, record.email)}
              title="Reenviar convite por email"
              size="small"
              style={{ 
                background: 'rgba(59, 130, 246, 0.7)', 
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                fontWeight: 500
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.9)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.7)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          )}
          <Button 
            icon={<DeleteOutlined />} 
            danger 
            onClick={() => handleDelete(record.id)} 
            title="Eliminar Utilizador"
            size="small"
          />
        </Space>
      ),
    },
  ];

  if (!currentUser || !isAdmin(getUserRole(currentUser))) {
    return (
      <div className="users-management-container">
        <div className="users-access-denied">
          <Title level={2}>Acesso Negado</Title>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
            Apenas administradores podem aceder a esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-management-container">
      <Card
        bodyStyle={{ 
          padding: '24px', 
          minHeight: 400, 
          background: 'transparent',
          borderRadius: '12px'
        }}
        style={{ 
          width: '100%', 
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
          borderRadius: '12px', 
          background: 'rgba(255, 255, 255, 0.15)', 
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {/* Header Section */}
        <div className="users-header-section">
          <div>
            <Title level={3} style={{ margin: 0, marginBottom: '8px', fontSize: 28, fontWeight: 700, color: '#ffffff' }}>
              Gestão de Utilizadores
            </Title>
            <Typography.Text style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.8)' }}>
              Gerir utilizadores e permissões do sistema
            </Typography.Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            onClick={() => { 
              setEditingUser(null); 
              form.resetFields(); 
              setShowModal(true); 
            }}
            style={{ 
              fontSize: 14, 
              height: '40px', 
              borderRadius: '8px', 
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}
          >
            Novo Utilizador
          </Button>
        </div>
        
        {/* Table Section */}
        <div style={{ overflowX: 'auto' }}>
          <Table 
            columns={columns} 
            dataSource={filteredUsers} 
            rowKey="id" 
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} utilizadores`,
              style: { marginTop: '24px' }
            }}
            style={{ marginTop: '24px' }}
          />
        </div>
      </Card>

      <Modal
        title={editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}
        open={showModal}
        onOk={handleModalOk}
        onCancel={() => { 
          setShowModal(false); 
          setEditingUser(null); 
          form.resetFields(); 
        }}
        width={600}
        okText={editingUser ? 'Atualizar' : 'Criar'}
        cancelText="Cancelar"
        okButtonProps={{
          style: {
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 1), rgba(37, 99, 235, 1))',
            border: 'none',
            fontWeight: 600
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item 
            name="username" 
            label="Nome de Utilizador" 
            rules={[{ required: true, message: 'Por favor insira o nome de utilizador!' }]}
          >
            <Input />
          </Form.Item>
          
          {!editingUser && (
            <>
              <Form.Item 
                name="given_name" 
                label="Nome" 
                rules={[
                  { required: true, message: 'Por favor insira o nome!' },
                  { min: 2, message: 'Nome deve ter pelo menos 2 caracteres' }
                ]}
              >
                <Input />
              </Form.Item>
              
              <Form.Item 
                name="family_name" 
                label="Apelido" 
                rules={[
                  { required: true, message: 'Por favor insira o apelido!' },
                  { min: 2, message: 'Apelido deve ter pelo menos 2 caracteres' }
                ]}
              >
                <Input />
              </Form.Item>
            </>
          )}
          
          <Form.Item 
            name="email" 
            label="Email" 
            rules={[
              { required: true, message: 'Por favor insira o email!' },
              { type: 'email', message: 'Por favor insira um email válido!' }
            ]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item 
            name="role" 
            label="Função" 
            rules={[{ required: true, message: 'Por favor selecione a função!' }]}
          > 
            <Select>
              <Option value="Admin">Admin</Option>
              <Option value="Diretor Comercial">Diretor Comercial</Option>
              <Option value="Team Leader">Team Leader</Option>
              <Option value="Gestor de Equipa">Gestor de Equipa</Option>
              <Option value="Gestor de Equipa Júnior">Gestor de Equipa Júnior</Option>
              <Option value="Gestor Comercial">Gestor Comercial</Option>
            </Select>
          </Form.Item>
          
          <Form.Item 
            name="business_area" 
            label="Área de Negócio" 
            rules={[{ required: true, message: 'Por favor selecione a área de negócio!' }]}
          > 
            <Select>
              <Option value="Admin">Admin</Option>
              <Option value="telecommunications">Telecomunicações</Option>
              <Option value="energy_solutions">Energia</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users; 